import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from './client';
import { qk } from './queryKeys';
import type {
  AdminBillingDto, ApplyRequest, BillingInfoDto, BillingOverviewDto, ClaimResponse, CycleSourcing, DeviceKind, Guid, InviteParticipantRequest,
  JobCardDto, JobCategory, JobDetailDto, LevelProgressDto, MemberShareDto, MyEarningsDto, MyParticipationDto, ParticipantDto, PayoutAccountDto, PayoutDto, StatementDto,
  TeamCardDto, TeamPublicDto, TeamWorkDto, TeamWorkItemDto, TesterCardDto, TesterProfileDto, TesterPublicDto, UpdatePayoutAccountRequest,
  TesterLevel, UpsertTesterProfileRequest, CvContent, CvDto, CvImportOptionsDto, CvImportResultDto, MeDto, TesterCvDto, RecommendationsDto,
} from './types';

export interface TesterFilters {
  q?: string | null; skill?: string | null; device?: DeviceKind | null; category?: JobCategory | null; minLevel?: TesterLevel | null; available?: boolean;
}
/** Public teams: name search and the number of active members (leads count). `sort: 'members'` puts the largest first. */
export interface TeamFilters { q?: string | null; minMembers?: number | null; maxMembers?: number | null; sort?: 'name' | 'members' | null }
export interface JobFilters { sourcing?: CycleSourcing | null; category?: JobCategory | null; eligible?: boolean; q?: string | null }

export const marketApi = {
  myProfile: () => api<TesterProfileDto | null>('/api/me/profile'),
  saveProfile: (body: UpsertTesterProfileRequest) => api<TesterProfileDto>('/api/me/profile', { method: 'PUT', body }),
  testers: (f: TesterFilters) => api<TesterCardDto[]>('/api/testers', {
    query: { q: f.q || null, skill: f.skill || null, device: f.device ?? null, category: f.category ?? null, minLevel: f.minLevel ?? null, available: f.available || null },
  }),
  myLevel: () => api<LevelProgressDto>('/api/me/level'),
  testerLevel: (userId: Guid) => api<LevelProgressDto>(`/api/admin/testers/${userId}/level`),
  setQualified: (userId: Guid, value: boolean) => api<LevelProgressDto>(`/api/admin/testers/${userId}/qualification`, { method: 'POST', body: { value } }),
  setVerified: (userId: Guid, value: boolean) => api<LevelProgressDto>(`/api/admin/testers/${userId}/identity`, { method: 'POST', body: { value } }),
  tester: (userId: Guid) => api<TesterPublicDto>(`/api/testers/${userId}`),
  myCv: () => api<CvDto>('/api/me/cv'),
  saveCv: (body: CvContent) => api<CvDto>('/api/me/cv', { method: 'PUT', body }),
  cvImportOptions: () => api<CvImportOptionsDto>('/api/me/cv/import'),
  importCv: ({ text, file }: { text: string; file?: File }) => {
    const form = new FormData();
    form.append('text', text);
    if (file) form.append('file', file);
    return api<CvImportResultDto>('/api/me/cv/import', { method: 'POST', body: form });
  },
  testerCv: (userId: Guid) => api<TesterCvDto>(`/api/testers/${userId}/cv`),

  teams: (f: TeamFilters) => api<TeamCardDto[]>('/api/teams', {
    query: { q: f.q || null, minMembers: f.minMembers ?? null, maxMembers: f.maxMembers ?? null, sort: f.sort === 'members' ? 'members' : null },
  }),
  team: (teamId: Guid) => api<TeamPublicDto>(`/api/teams/${teamId}`),
  join: (teamId: Guid, message: string | null) => api<TeamCardDto>(`/api/teams/${teamId}/join-requests`, { method: 'POST', body: { message } }),
  cancelJoin: (teamId: Guid) => api<void>(`/api/teams/${teamId}/join-requests`, { method: 'DELETE' }),
  shares: (teamId: Guid, shares: MemberShareDto[]) => api<MemberShareDto[]>(`/api/communities/${teamId}/shares`, { method: 'PUT', body: { shares } }),
  teamWork: (teamId: Guid) => api<TeamWorkDto>(`/api/communities/${teamId}/work`),
  reassign: (assignmentId: Guid, userId: Guid) => api<TeamWorkItemDto>(`/api/assignments/${assignmentId}/reassign`, { method: 'POST', body: { userId } }),

  jobs: (f: JobFilters) => api<JobCardDto[]>('/api/jobs', { query: { sourcing: f.sourcing ?? null, category: f.category ?? null, eligible: f.eligible || null, q: f.q || null } }),
  job: (cycleId: Guid) => api<JobDetailDto>(`/api/jobs/${cycleId}`),
  apply: (cycleId: Guid, body: ApplyRequest) => api<MyParticipationDto>(`/api/jobs/${cycleId}/applications`, { method: 'POST', body }),
  claim: (cycleId: Guid, teamId: Guid | null) => api<ClaimResponse>(`/api/jobs/${cycleId}/claims`, { method: 'POST', body: { teamId } }),
  withdraw: (participantId: Guid) => api<void>(`/api/participants/${participantId}/withdraw`, { method: 'POST' }),
  respond: (participantId: Guid, accept: boolean) => api<MyParticipationDto>(`/api/participants/${participantId}/respond`, { method: 'POST', body: { accept } }),
  participations: () => api<MyParticipationDto[]>('/api/my/participations'),

  participants: (cycleId: Guid) => api<ParticipantDto[]>(`/api/cycles/${cycleId}/participants`),
  decide: (participantId: Guid, accept: boolean, note: string | null) =>
    api<ParticipantDto>(`/api/participants/${participantId}/decision`, { method: 'POST', body: { accept, note } }),
  invite: (cycleId: Guid, body: InviteParticipantRequest) => api<ParticipantDto>(`/api/cycles/${cycleId}/participants`, { method: 'POST', body }),
  recommendations: (cycleId: Guid, limit = 6) => api<RecommendationsDto>(`/api/cycles/${cycleId}/recommendations`, { query: { limit } }),
  removeParticipant: (participantId: Guid) => api<void>(`/api/participants/${participantId}`, { method: 'DELETE' }),

  earnings: () => api<MyEarningsDto>('/api/my/earnings'),
  payoutAccount: () => api<PayoutAccountDto>('/api/me/payout-account'),
  savePayoutAccount: (body: UpdatePayoutAccountRequest) => api<PayoutAccountDto>('/api/me/payout-account', { method: 'PUT', body }),

  billing: (orgId: Guid) => api<BillingOverviewDto>(`/api/organizations/${orgId}/billing`),
  saveBilling: (orgId: Guid, body: BillingInfoDto) => api<BillingInfoDto>(`/api/organizations/${orgId}/billing`, { method: 'PUT', body }),
  createStatement: (orgId: Guid, cycleId: Guid) => api<StatementDto>(`/api/organizations/${orgId}/statements`, { method: 'POST', body: { cycleId } }),
  statement: (id: Guid) => api<StatementDto>(`/api/statements/${id}`),
  cancelStatement: (id: Guid) => api<StatementDto>(`/api/statements/${id}/cancel`, { method: 'POST' }),
  pay: (id: Guid) => api<{ url: string }>(`/api/statements/${id}/pay`, { method: 'POST' }),
  admin: () => api<AdminBillingDto>('/api/admin/billing'),
  markPaid: (id: Guid, reference: string | null) => api<StatementDto>(`/api/admin/statements/${id}/mark-paid`, { method: 'POST', body: { reference } }),
  release: (id: Guid) => api<StatementDto>(`/api/admin/statements/${id}/release`, { method: 'POST' }),
  markPaidOut: (userId: Guid, reference: string | null) => api<PayoutDto>('/api/admin/payouts', { method: 'POST', body: { userId, reference } }),
};

// ----------------------------------------------------------------------------- profile & directory
export function useMyProfile() {
  return useQuery({ queryKey: qk.market.profile, queryFn: marketApi.myProfile });
}

export function useSaveProfile() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: marketApi.saveProfile,
    onSuccess: (p) => {
      qc.setQueryData(qk.market.profile, p);
      void qc.invalidateQueries({ queryKey: qk.me });
      void qc.invalidateQueries({ queryKey: ['market', 'jobs'] });
    },
  });
}

export function useTesters(filters: TesterFilters, enabled = true) {
  return useQuery({ queryKey: qk.market.testers(filters), queryFn: () => marketApi.testers(filters), placeholderData: keepPreviousData, enabled });
}

export function useMyLevel(enabled = true) {
  return useQuery({ queryKey: qk.market.level, queryFn: marketApi.myLevel, enabled });
}

/** Platform admins: a tester's level details, to grant the academy or the identity check. */
export function useTesterLevel(userId: Guid | undefined, enabled: boolean) {
  return useQuery({ queryKey: [...qk.market.tester(userId ?? ''), 'level'] as const, queryFn: () => marketApi.testerLevel(userId!), enabled: !!userId && enabled });
}

export function useSetTesterFlag(userId: Guid) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ flag, value }: { flag: 'qualified' | 'verified'; value: boolean }) =>
      flag === 'qualified' ? marketApi.setQualified(userId, value) : marketApi.setVerified(userId, value),
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.market.all }),
  });
}

export function useTester(userId: Guid | undefined) {
  return useQuery({ queryKey: qk.market.tester(userId ?? ''), queryFn: () => marketApi.tester(userId!), enabled: !!userId, retry: false });
}

// ----------------------------------------------------------------------------- CV
export function useMyCv(enabled = true) {
  return useQuery({ queryKey: qk.market.cv, queryFn: marketApi.myCv, enabled });
}

export function useCvImportOptions(enabled = true) {
  return useQuery({ queryKey: [...qk.market.cv, 'import'] as const, queryFn: marketApi.cvImportOptions, enabled, staleTime: 5 * 60_000 });
}

/** Reads a CV draft from a PDF's text (and the PDF itself when AI reading is on). Saves nothing. */
export function useImportCv() {
  return useMutation({ mutationFn: marketApi.importCv });
}

export function useSaveCv() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: marketApi.saveCv,
    onSuccess: (cv) => {
      qc.setQueryData(qk.market.cv, cv);
      // The sign-up step is done: let the app open before /api/me is fetched again.
      qc.setQueryData<MeDto>(qk.me, (me) => (me ? { ...me, needsCv: false } : me));
      void qc.invalidateQueries({ queryKey: qk.me });
      void qc.invalidateQueries({ queryKey: ['market', 'tester'] });
      void qc.invalidateQueries({ queryKey: ['market', 'testerCv'] });
    },
  });
}

export function useTesterCv(userId: Guid | undefined, enabled = true) {
  return useQuery({ queryKey: qk.market.testerCv(userId ?? ''), queryFn: () => marketApi.testerCv(userId!), enabled: !!userId && enabled, retry: false });
}

// ----------------------------------------------------------------------------- teams
export function useTeams(filters: TeamFilters | string, enabled = true) {
  const f: TeamFilters = typeof filters === 'string' ? { q: filters } : filters;
  return useQuery({ queryKey: qk.market.teams(f), queryFn: () => marketApi.teams(f), placeholderData: keepPreviousData, enabled });
}

export function useTeam(teamId: Guid | undefined) {
  return useQuery({ queryKey: qk.market.team(teamId ?? ''), queryFn: () => marketApi.team(teamId!), enabled: !!teamId, retry: false });
}

function useInvalidateTeams() {
  const qc = useQueryClient();
  return () => {
    void qc.invalidateQueries({ queryKey: ['market', 'teams'] });
    void qc.invalidateQueries({ queryKey: ['market', 'team'] });
    void qc.invalidateQueries({ queryKey: qk.communities.all });
    void qc.invalidateQueries({ queryKey: qk.me });
  };
}

export function useJoinTeam() {
  const invalidate = useInvalidateTeams();
  return useMutation({ mutationFn: ({ teamId, message }: { teamId: Guid; message: string | null }) => marketApi.join(teamId, message), onSuccess: invalidate });
}

export function useCancelJoin() {
  const invalidate = useInvalidateTeams();
  return useMutation({ mutationFn: marketApi.cancelJoin, onSuccess: invalidate });
}

export function useSaveShares(teamId: Guid) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (shares: MemberShareDto[]) => marketApi.shares(teamId, shares),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qk.communities.detail(teamId) });
      void qc.invalidateQueries({ queryKey: qk.market.teamWork(teamId) });
      void qc.invalidateQueries({ queryKey: qk.team.panel(teamId) });
    },
  });
}

export function useTeamWork(teamId: Guid | undefined, enabled = true) {
  return useQuery({ queryKey: qk.market.teamWork(teamId ?? ''), queryFn: () => marketApi.teamWork(teamId!), enabled: !!teamId && enabled });
}

export function useReassign(teamId: Guid) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ assignmentId, userId }: { assignmentId: Guid; userId: Guid }) => marketApi.reassign(assignmentId, userId),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qk.market.teamWork(teamId) });
      void qc.invalidateQueries({ queryKey: qk.team.panel(teamId) });
      void qc.invalidateQueries({ queryKey: qk.my.assignments });
    },
  });
}

// ----------------------------------------------------------------------------- jobs
export function useJobs(filters: JobFilters) {
  return useQuery({ queryKey: qk.market.jobs(filters), queryFn: () => marketApi.jobs(filters), placeholderData: keepPreviousData });
}

export function useJob(cycleId: Guid | undefined) {
  return useQuery({ queryKey: qk.market.job(cycleId ?? ''), queryFn: () => marketApi.job(cycleId!), enabled: !!cycleId, retry: false });
}

export function useMyParticipations() {
  return useQuery({ queryKey: qk.market.participations, queryFn: marketApi.participations });
}

function useInvalidateJobs() {
  const qc = useQueryClient();
  return () => {
    void qc.invalidateQueries({ queryKey: ['market', 'jobs'] });
    void qc.invalidateQueries({ queryKey: ['market', 'job'] });
    void qc.invalidateQueries({ queryKey: qk.market.participations });
    void qc.invalidateQueries({ queryKey: qk.my.all });
    void qc.invalidateQueries({ queryKey: ['market', 'teamWork'] });
  };
}

export function useApply() {
  const invalidate = useInvalidateJobs();
  return useMutation({ mutationFn: ({ cycleId, body }: { cycleId: Guid; body: ApplyRequest }) => marketApi.apply(cycleId, body), onSuccess: invalidate });
}

export function useClaim() {
  const invalidate = useInvalidateJobs();
  return useMutation({ mutationFn: ({ cycleId, teamId }: { cycleId: Guid; teamId: Guid | null }) => marketApi.claim(cycleId, teamId), onSuccess: invalidate });
}

export function useWithdraw() {
  const invalidate = useInvalidateJobs();
  return useMutation({ mutationFn: marketApi.withdraw, onSuccess: invalidate });
}

export function useRespondInvite() {
  const invalidate = useInvalidateJobs();
  return useMutation({ mutationFn: ({ participantId, accept }: { participantId: Guid; accept: boolean }) => marketApi.respond(participantId, accept), onSuccess: invalidate });
}

// ----------------------------------------------------------------------------- customer: participants
export function useParticipants(cycleId: Guid | undefined, enabled = true) {
  return useQuery({ queryKey: qk.market.participants(cycleId ?? ''), queryFn: () => marketApi.participants(cycleId!), enabled: !!cycleId && enabled });
}

/** Testers who fit a marketplace job (people already on it are left out). */
export function useRecommendations(cycleId: Guid, enabled = true) {
  return useQuery({ queryKey: ['market', 'recommendations', cycleId], queryFn: () => marketApi.recommendations(cycleId), enabled, staleTime: 60_000 });
}

function useInvalidateParticipants(cycleId: Guid) {
  const qc = useQueryClient();
  return () => {
    void qc.invalidateQueries({ queryKey: ['market', 'recommendations', cycleId] });
    void qc.invalidateQueries({ queryKey: qk.market.participants(cycleId) });
    void qc.invalidateQueries({ queryKey: qk.cycles.board(cycleId) });
    void qc.invalidateQueries({ queryKey: qk.cycles.all });
  };
}

export function useDecideParticipant(cycleId: Guid) {
  const invalidate = useInvalidateParticipants(cycleId);
  return useMutation({
    mutationFn: ({ participantId, accept, note }: { participantId: Guid; accept: boolean; note: string | null }) => marketApi.decide(participantId, accept, note),
    onSuccess: invalidate,
  });
}

export function useInviteParticipant(cycleId: Guid) {
  const invalidate = useInvalidateParticipants(cycleId);
  return useMutation({ mutationFn: (body: InviteParticipantRequest) => marketApi.invite(cycleId, body), onSuccess: invalidate });
}

export function useRemoveParticipant(cycleId: Guid) {
  const invalidate = useInvalidateParticipants(cycleId);
  return useMutation({ mutationFn: marketApi.removeParticipant, onSuccess: invalidate });
}

// ----------------------------------------------------------------------------- earnings & payouts
export function useMyEarnings() {
  return useQuery({ queryKey: qk.market.earnings, queryFn: marketApi.earnings });
}

export function useSavePayoutAccount() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: marketApi.savePayoutAccount,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qk.market.earnings });
      void qc.invalidateQueries({ queryKey: qk.market.payoutAccount });
    },
  });
}

// ----------------------------------------------------------------------------- billing
export function useBilling(orgId: Guid | undefined, enabled = true) {
  return useQuery({ queryKey: qk.billing.overview(orgId ?? ''), queryFn: () => marketApi.billing(orgId!), enabled: !!orgId && enabled });
}

export function useSaveBilling(orgId: Guid) {
  const qc = useQueryClient();
  return useMutation({ mutationFn: (body: BillingInfoDto) => marketApi.saveBilling(orgId, body), onSuccess: () => qc.invalidateQueries({ queryKey: qk.billing.all }) });
}

export function useCreateStatement(orgId: Guid) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (cycleId: Guid) => marketApi.createStatement(orgId, cycleId),
    onSuccess: (s) => {
      qc.setQueryData(qk.billing.statement(s.summary.id), s);
      void qc.invalidateQueries({ queryKey: qk.billing.all });
    },
  });
}

export function useStatement(id: Guid | undefined) {
  return useQuery({ queryKey: qk.billing.statement(id ?? ''), queryFn: () => marketApi.statement(id!), enabled: !!id, retry: false });
}

function useStatementMutation<T>(fn: (arg: T) => Promise<StatementDto>) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: (s) => {
      qc.setQueryData(qk.billing.statement(s.summary.id), s);
      void qc.invalidateQueries({ queryKey: qk.billing.all });
    },
  });
}

export const useCancelStatement = () => useStatementMutation(marketApi.cancelStatement);
export const useMarkPaid = () => useStatementMutation(({ id, reference }: { id: Guid; reference: string | null }) => marketApi.markPaid(id, reference));
export const useRelease = () => useStatementMutation(marketApi.release);

export function usePayStatement() {
  return useMutation({ mutationFn: marketApi.pay });
}

export function useAdminBilling(enabled = true) {
  return useQuery({ queryKey: qk.billing.admin, queryFn: marketApi.admin, enabled });
}

export function useMarkPaidOut() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ userId, reference }: { userId: Guid; reference: string | null }) => marketApi.markPaidOut(userId, reference),
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.billing.all }),
  });
}
